/*----------------------------------
- LEGAL PAGE
- One shell for /terms, /privacy and /refund-policy. Legal copy is prose, so the
- page stays a long readable column with no CTA, no related links and no ask-AI
- block: nothing here should compete with the text. What surrounds it is purely
- navigation, a switcher between the three documents and a section index, plus
- the trader identification the documents all refer to.
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import React from 'react';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import type { LegalPageContent } from '@/client/catalogs/landing/legal/types';
import { LEGAL_DOCUMENT_TABS } from '@/client/catalogs/landing/legal/documents';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import {
    HERO_SECTION_CLASS,
    PAGE_CONTAINER_CLASS,
    PAGE_LEAD_CLASS,
    PAGE_TITLE_CLASS,
    SEGMENTED_OPTION_ACTIVE_CLASS,
    SEGMENTED_OPTION_CLASS,
    SEGMENTED_TRACK_CLASS,
} from '@/client/pages/landing/components/shared/pageStyles';
import {
    LEGAL_ENTITY,
    LEGAL_PAGES_LAST_UPDATED,
    isLegalEntityResolved,
    legalEntityIdentityParts,
    legalEntityValue,
} from '@/common/catalogs/legal/entity';
import { track } from '@/client/pages/landing/lib/analytics';

import '../../shared.css';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** Shared underline treatment for the links inside the company card. */
const COMPANY_CARD_LINK_CLASS =
    'underline underline-offset-4 decoration-[#c9c8c2] transition hover:decoration-[#1b1d1e]';

/** One identification row: label above value on a phone, two columns from `lg` up. */
const COMPANY_CARD_ROW_CLASS =
    'border-t border-[#f1ede6] py-2.5 text-[13.5px] leading-[1.5] lg:grid lg:grid-cols-[130px_minmax(0,1fr)] lg:gap-3';

/**
 * The section index, stuck under the phone top bar as a scrollable chip row and
 * beside the text as a column from `lg` up. Two shapes, one element: duplicating
 * fourteen links to get two layouts would double the DOM of every legal page.
 */
const SECTION_NAV_CLASS = [
    'sticky top-[var(--ud-topbar-h)] z-20 -mx-5 flex gap-1.5 overflow-x-auto border-y border-[rgba(27,29,30,0.06)]',
    'bg-[rgba(248,246,241,0.94)] px-5 py-2.5 backdrop-blur-md [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
    'lg:top-24 lg:z-auto lg:mx-0 lg:flex-col lg:gap-0.5 lg:self-start lg:overflow-visible lg:border-0',
    'lg:bg-transparent lg:p-0 lg:backdrop-blur-none',
].join(' ');

const SECTION_NAV_LINK_CLASS = [
    'inline-flex min-h-[34px] flex-none items-center gap-1.5 whitespace-nowrap rounded-full border border-[#e6e2da]',
    'bg-white px-3 text-[12.5px] font-semibold text-[#4a4d4e]',
    'lg:min-h-0 lg:gap-2.5 lg:rounded-[10px] lg:border-0 lg:bg-transparent lg:px-2.5 lg:py-2 lg:text-[13.5px]',
    'lg:text-[#66696b] lg:transition-colors lg:hover:bg-[#f0ede6] lg:hover:text-[#1b1d1e]',
].join(' ');

/**
 * A section has to clear the top bar and, on a phone, the chip row stuck under it
 * (10px + 34px + 10px plus its two hairlines) before its heading is readable.
 */
const SECTION_SCROLL_MARGIN_CLASS = 'scroll-mt-[calc(var(--ud-topbar-h)+60px)] lg:scroll-mt-28';

/*----------------------------------
- HELPERS
----------------------------------*/

/**
 * Catalogued headings read "4. Plans and billing". The number drives the chips
 * and the index, the rest is the label, so neither is retyped beside the legal
 * copy it would then be free to contradict. A heading without that prefix still
 * resolves, it simply carries no number.
 */
const splitSectionHeading = (heading: string): { number: string; label: string } => {
    const numbered = /^(\d+)\.\s+(.+)$/.exec(heading);

    return numbered ? { number: numbered[1], label: numbered[2] } : { number: '', label: heading };
};

/*----------------------------------
- COMPONENT
----------------------------------*/

type LegalPageProps = {
    content: LegalPageContent;
    currentPath: string;
    trackingPage: string;
    navigation?: LandingHeaderDomainsNavigation;
};

const LegalPage = ({
    content,
    currentPath,
    trackingPage,
    navigation = EMPTY_LANDING_NAVIGATION,
}: LegalPageProps) => {
    /*----------------------------------
    - INIT
    ----------------------------------*/

    const entityResolved = isLegalEntityResolved();
    const identity = legalEntityIdentityParts();
    const sections = content.sections.map((section) => ({
        ...section,
        ...splitSectionHeading(section.heading),
    }));

    /*----------------------------------
    - EFFECTS
    ----------------------------------*/

    React.useEffect(() => {
        if (typeof window === 'undefined') return;

        track('page_view', {
            page: trackingPage,
            path: window.location.pathname + window.location.search,
        });
    }, [trackingPage]);

    /*----------------------------------
    - RENDER
    ----------------------------------*/

    return (
        <div class="ud-landing-shell min-h-screen text-slate-950">
            <div class="relative z-10">
                <Header
                    links={LANDING_WEBSITE_LINKS}
                    navigation={navigation}
                    currentPath={currentPath}
                    trackingPage={trackingPage}
                    // A legal document has nothing to sell, so the phone keeps its full height.
                    mobileActions={null}
                />

                <main id="top" class={PAGE_CONTAINER_CLASS}>
                    <section
                        class={`${HERO_SECTION_CLASS} grid gap-6 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-end lg:gap-16`}
                    >
                        <div>
                            <nav
                                aria-label="Legal documents"
                                class={`${SEGMENTED_TRACK_CLASS} w-full lg:w-auto`}
                            >
                                {LEGAL_DOCUMENT_TABS.map((tab) => {
                                    const isCurrent = tab.path === currentPath;

                                    return (
                                        <a
                                            key={tab.path}
                                            href={tab.path}
                                            aria-current={isCurrent ? 'page' : undefined}
                                            class={[
                                                SEGMENTED_OPTION_CLASS,
                                                'flex-1 lg:flex-none',
                                                isCurrent ? SEGMENTED_OPTION_ACTIVE_CLASS : '',
                                            ].join(' ')}
                                        >
                                            {tab.label}
                                        </a>
                                    );
                                })}
                            </nav>

                            <h1 class={`${PAGE_TITLE_CLASS} mt-6 lg:mt-7`}>{content.hero.heading}</h1>
                            <p class={`${PAGE_LEAD_CLASS} mt-3.5 lg:mt-5 lg:max-w-[620px]`}>
                                {content.hero.intro}
                            </p>
                            <p class="mt-3 text-[13px] text-[#66696b] lg:mt-3.5">
                                Last updated {LEGAL_PAGES_LAST_UPDATED}
                            </p>
                        </div>

                        <div class="rounded-[20px] border border-[#e6e3db] bg-white/70 px-4 py-1.5 lg:rounded-[22px] lg:px-5 lg:py-2">
                            <h2 class="pb-1.5 pt-3 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#66696b]">
                                The company behind Unique Domains
                            </h2>
                            <dl class="m-0">
                                <div class={COMPANY_CARD_ROW_CLASS}>
                                    <dt class="text-[#66696b]">Registered company</dt>
                                    <dd class="mt-0.5 text-[#1b1d1e] lg:mt-0">
                                        <a
                                            href={LEGAL_ENTITY.registerUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            class={COMPANY_CARD_LINK_CLASS}
                                        >
                                            {identity.name}
                                        </a>
                                        , {identity.details}
                                    </dd>
                                </div>
                                <div class={COMPANY_CARD_ROW_CLASS}>
                                    <dt class="text-[#66696b]">Registered address</dt>
                                    <dd class="mt-0.5 text-[#1b1d1e] lg:mt-0">
                                        {legalEntityValue(LEGAL_ENTITY.address)},{' '}
                                        {legalEntityValue(LEGAL_ENTITY.country)}
                                    </dd>
                                </div>
                                <div class={COMPANY_CARD_ROW_CLASS}>
                                    <dt class="text-[#66696b]">Company number</dt>
                                    <dd class="mt-0.5 text-[#1b1d1e] lg:mt-0">
                                        {legalEntityValue(LEGAL_ENTITY.registrationNumber)}
                                    </dd>
                                </div>
                                <div class={COMPANY_CARD_ROW_CLASS}>
                                    <dt class="text-[#66696b]">VAT number</dt>
                                    <dd class="mt-0.5 text-[#1b1d1e] lg:mt-0">
                                        {legalEntityValue(LEGAL_ENTITY.vatNumber)}
                                    </dd>
                                </div>
                                <div class={COMPANY_CARD_ROW_CLASS}>
                                    <dt class="text-[#66696b]">Publication director</dt>
                                    <dd class="mt-0.5 text-[#1b1d1e] lg:mt-0">
                                        <a
                                            href={LEGAL_ENTITY.publicationDirectorUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            class={COMPANY_CARD_LINK_CLASS}
                                        >
                                            {legalEntityValue(LEGAL_ENTITY.publicationDirector)}
                                        </a>
                                    </dd>
                                </div>
                                <div class={COMPANY_CARD_ROW_CLASS}>
                                    <dt class="text-[#66696b]">Contact</dt>
                                    <dd class="mt-0.5 text-[#1b1d1e] lg:mt-0">
                                        <a
                                            href={`mailto:${LEGAL_ENTITY.contactEmail}`}
                                            class={COMPANY_CARD_LINK_CLASS}
                                        >
                                            {LEGAL_ENTITY.contactEmail}
                                        </a>
                                    </dd>
                                </div>
                                <div class={COMPANY_CARD_ROW_CLASS}>
                                    <dt class="text-[#66696b]">Hosted by</dt>
                                    <dd class="mt-0.5 text-[#1b1d1e] lg:mt-0">
                                        {legalEntityValue(LEGAL_ENTITY.hostName)},{' '}
                                        {legalEntityValue(LEGAL_ENTITY.hostAddress)}
                                    </dd>
                                </div>
                            </dl>

                            {!entityResolved ? (
                                <p class="border-t border-[#e6e3db] py-3 text-[13px] leading-[1.6] text-[#8a6d1f]">
                                    Some company details are still being completed. This page is not
                                    published to search engines until they are.
                                </p>
                            ) : null}
                        </div>
                    </section>

                    {/*
                        Block flow on a phone so the chip row can stick across the whole
                        document; a grid would trap it in its own single-column row and it
                        would scroll away with the first section.
                    */}
                    <div class="mt-7 pb-14 lg:mt-16 lg:grid lg:grid-cols-[260px_minmax(0,760px)] lg:justify-start lg:gap-20 lg:pb-24">
                        <nav aria-label="Sections" class={SECTION_NAV_CLASS}>
                            {sections.map((section) => (
                                <a key={section.id} href={`#${section.id}`} class={SECTION_NAV_LINK_CLASS}>
                                    <span class="text-[#6a695e] lg:w-5 lg:tabular-nums">{section.number}</span>
                                    {section.label}
                                </a>
                            ))}
                        </nav>

                        <div class="pt-2 lg:pt-0">
                            {sections.map((section) => (
                                <section
                                    key={section.id}
                                    id={section.id}
                                    class={`${SECTION_SCROLL_MARGIN_CLASS} pt-7 lg:pb-10 lg:pt-0`}
                                >
                                    <h2 class="text-[21px] font-semibold leading-[1.3] tracking-[-0.015em] text-[#1b1d1e] lg:text-[26px] lg:leading-[1.25] lg:tracking-[-0.02em]">
                                        {section.heading}
                                    </h2>

                                    {section.blocks.map((block, index) =>
                                        block.kind === 'paragraph' ? (
                                            <p
                                                key={`${section.id}-p-${index}`}
                                                class="mt-3 text-[15px] leading-[1.75] text-[#4a4d4e] lg:mt-3.5 lg:text-[16.5px]"
                                            >
                                                {block.text}
                                            </p>
                                        ) : (
                                            <ul
                                                key={`${section.id}-l-${index}`}
                                                class="mt-3 flex list-disc flex-col gap-2 pl-[18px] text-[15px] leading-[1.7] text-[#4a4d4e] lg:mt-3.5 lg:gap-2.5 lg:pl-5 lg:text-[16.5px]"
                                            >
                                                {block.items.map((item) => (
                                                    <li key={item}>{item}</li>
                                                ))}
                                            </ul>
                                        ),
                                    )}
                                </section>
                            ))}
                        </div>
                    </div>
                </main>

                <Footer links={LANDING_WEBSITE_LINKS} sectionHrefPrefix="/" trackingPage={trackingPage} />
            </div>
        </div>
    );
};

export default LegalPage;
